import styled from '@emotion/styled'
import Button from '@mui/material/Button'
import { useCallback, forwardRef, PropsWithChildren, FC } from 'react'
import React from 'react'

import { ModalAction } from '../../../../__generated__/api'
import { Text } from '../../../../common/components/Typography/Typography'
import { useSearchEvents } from '../../../../xstate/events'
import { SearchTargetValue } from '../../../../xstate/types'

const ButtonList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
`

const ModalButton = styled(Button)`
  height: 3.5rem;
  border-radius: 6.2rem;
`

const ModalText: FC<PropsWithChildren> = ({ children }) => (
  <Text $size={300} $weight="Medium" $height="Small">
    {children}
  </Text>
)

interface ModalActionProps {
  actions: ModalAction[]
  onClose: () => void
  onContinue: () => void
  onDigitalClinic?: (queue: string) => void
  onNode?: (nodeId: string) => void
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

const ModalActionButtons = forwardRef<HTMLButtonElement, ModalActionProps>(
  (
    {
      actions,
      onClose,
      onContinue,
      onDigitalClinic,
      onNode,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
    },
    ref
  ) => {
    const { setSearchTarget } = useSearchEvents()

    const getSortedActions = useCallback((modalActions: ModalAction[]): ModalAction[] => {
      return [...modalActions].sort((a, b) => {
        if (a.appearance === 'primary') return -1
        if (b.appearance === 'primary') return 1
        if (a.appearance === 'secondary') return -1
        if (b.appearance === 'secondary') return 1
        return 0
      })
    }, [])

    const getButtonVariant = (appearance?: string): 'outlined' | 'text' | undefined => {
      if (appearance === 'secondary') return 'outlined'
      if (appearance === 'tertiary') return 'text'
      return undefined
    }

    const handleNodeSelection = useCallback(
      (nodeId: string) => {
        setSearchTarget({
          id: nodeId,
          value: SearchTargetValue.Node,
        })

        if (onNode) {
          onNode(nodeId)
        } else {
          onClose()
        }
      },
      [onNode, onClose, setSearchTarget]
    )

    const handleButtonClick = useCallback(
      (action: ModalAction) => {
        switch (action.type) {
          case 'close':
            return onClose
          case 'continue':
            return onContinue
          case 'digital_clinic':
            return () => onDigitalClinic?.(action.queue || '')
          case 'node':
            return () => action.node && handleNodeSelection(action.node)
          default:
            return () => {}
        }
      },
      [onClose, onContinue, onDigitalClinic, handleNodeSelection]
    )

    const sortedActions = getSortedActions(actions)

    return (
      <ButtonList aria-labelledby={ariaLabelledBy} aria-describedby={ariaDescribedBy}>
        {sortedActions.map((action, index) => {
          const variant = getButtonVariant(action.appearance)

          return (
            <li key={action.id}>
              {action.type !== 'url' ? (
                <ModalButton
                  ref={index === 0 ? ref : undefined}
                  variant={variant}
                  onClick={handleButtonClick(action)}
                  size="large"
                  fullWidth
                >
                  <ModalText>{action.title}</ModalText>
                </ModalButton>
              ) : (
                <ModalButton
                  ref={index === 0 ? ref : undefined}
                  fullWidth
                  variant={variant}
                  href={action.url || ''}
                  size="large"
                >
                  <ModalText>{action.title}</ModalText>
                </ModalButton>
              )}
            </li>
          )
        })}
      </ButtonList>
    )
  }
)

export default ModalActionButtons
